/** Shared settings role cohorts (safe for client + server imports). */

import { USER_ROLE_VALUES } from '@statura/types';

/** Portal actors. They manage notification choices through the portal, which
 * has its own surface and its own controlled RPC. */
const PORTAL_ROLES = new Set<string>(['CLIENT', 'FAMILY_CARER']);

/**
 * Every internal role, i.e. everyone who works for the provider.
 *
 * This is the cohort for a person's OWN notification preferences. That page is
 * not tenant configuration — it decides which emails and bell notices land on
 * one individual — so gating it on the settings admin default locked 77 of one
 * live provider's 89 staff out of a page that every notification email footer,
 * the unsubscribe link, the command palette and the Help Centre article all
 * send them to. They got "Access denied" and no way to turn anything off.
 *
 * Derived from `USER_ROLE_VALUES` rather than hand-listed so a new role is
 * admitted by default. The failure mode of forgetting a role here is a person
 * who cannot mute their own notifications; the failure mode of admitting one
 * wrongly is that they can mute their own notifications. The first is worse.
 *
 * Must stay in lockstep with the actor gate on
 * `upsert_my_notification_preference_controlled` — a cohort wider than the RPC
 * shows a page whose every toggle is refused at the database.
 */
export const SETTINGS_OWN_NOTIFICATION_ROLES = USER_ROLE_VALUES.filter(
  (role) => !PORTAL_ROLES.has(role),
) as readonly string[];

/**
 * The one settings leaf that is personal rather than tenant configuration.
 * Lives here, not in `settings-access.ts`, because that module is
 * `server-only` and the command palette needs this predicate on the client.
 */
export const OWN_NOTIFICATION_PREFERENCE_PATH = '/settings/notification-preferences';

export function isOwnNotificationPreferencePath(pathname: string): boolean {
  return (
    pathname === OWN_NOTIFICATION_PREFERENCE_PATH ||
    pathname.startsWith(`${OWN_NOTIFICATION_PREFERENCE_PATH}/`)
  );
}

export const SETTINGS_TENANT_ADMIN_ROLES = ['SYSTEM_ADMIN', 'TENANT_ADMIN'] as const;

export const SETTINGS_SYSTEM_OPERATOR_ROLES = ['SYSTEM_ADMIN'] as const;

export const SETTINGS_FINANCE_ROLES = ['SYSTEM_ADMIN', 'TENANT_ADMIN', 'FINANCE_OFFICER'] as const;

export const SETTINGS_PAY_ROLE_ROLES = [
  'SYSTEM_ADMIN',
  'TENANT_ADMIN',
  'FINANCE_OFFICER',
  'FACILITY_MANAGER',
  'PAYROLL_OFFICER',
] as const;

export const SETTINGS_PAYROLL_PROVIDER_ROLES = [
  'SYSTEM_ADMIN',
  'TENANT_ADMIN',
  'FINANCE_OFFICER',
  'PAYROLL_OFFICER',
] as const;

export const SETTINGS_XERO_ROLES = [
  'OWNER',
  'SYSTEM_ADMIN',
  'TENANT_ADMIN',
  'FINANCE_OFFICER',
  'PAYROLL_OFFICER',
] as const;

export const SETTINGS_INTEGRATIONS_HUB_ROLES = ['OWNER', 'TENANT_ADMIN'] as const;

export const SETTINGS_SERVICES_AUSTRALIA_ROLES = ['OWNER', 'SYSTEM_ADMIN', 'TENANT_ADMIN'] as const;

export const SETTINGS_RETENTION_ROLES = [
  'SYSTEM_ADMIN',
  'TENANT_ADMIN',
  'QUALITY_OFFICER',
] as const;

export const SIGNATURE_TEMPLATE_SETTINGS_ROLES = [
  'SYSTEM_ADMIN',
  'TENANT_ADMIN',
  'QUALITY_OFFICER',
  'CLINICAL_LEAD',
] as const;

/** Rate cards and service agreement pricing configuration. */
export const SETTINGS_PRICING_ROLES = [
  'SYSTEM_ADMIN',
  'TENANT_ADMIN',
  'REGION_FINANCE_OFFICER',
  'FINANCE_OFFICER',
  'COORDINATOR',
  'CASE_MANAGER',
] as const;

/**
 * The Settings rail's "Service agreements" leaf keeps main's audience exactly
 * (P1-5, 4 Nov 2026). Coordinators gained view-only access to the page, but
 * they reach it from the client record and the URL, not from the Settings rail,
 * whose frozen visibility is pinned by settings-delegation-main-parity.
 */
export const SETTINGS_PRICING_AGREEMENTS_ROLES = [
  'SYSTEM_ADMIN',
  'TENANT_ADMIN',
  'REGION_FINANCE_OFFICER',
  'FINANCE_OFFICER',
] as const;

/** Worker screening register — aligned with `/compliance/screening` leaf gate. */
export const SETTINGS_SCREENING_ROLES = [
  'SYSTEM_ADMIN',
  'TENANT_ADMIN',
  'REGION_MANAGER',
  'REGION_COMPLIANCE_OFFICER',
  'FACILITY_MANAGER',
  'COORDINATOR',
  'CLINICAL_LEAD',
  'QUALITY_OFFICER',
  'TEAM_LEADER',
] as const;
